import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Badge,
} from '@/components/ui';
import { MerchantCell } from '@/components/shared/merchant-cell';
import { formatCents } from '@/lib/utils';
import type { Service } from '@/types/service';
import { RoutingStatusBadge } from './routing-status-badge';
import { ServiceActions } from './service-actions';

interface ServiceTableProps {
  services: Service[];
  onEdit?: (service: Service) => void;
  onDelete?: (service: Service) => void;
  /** Spec 0219: show the merchant column for platform roles, hide for merchant roles. */
  showMerchant?: boolean;
}

/**
 * Spec 352 §5: table view for /services — a port of merchant-table.tsx. Clickable
 * rows navigate to the same target as ServiceCard; the action menu is the shared
 * ServiceActions. Consumes the SAME filtered+paginated list as the card view; the
 * page-level toggle swaps only this renderer (parity).
 */
export function ServiceTable({ services, onEdit, onDelete, showMerchant = true }: ServiceTableProps) {
  const { t } = useTranslation('services');
  const navigate = useNavigate();

  const openService = (service: Service) =>
    navigate(`/services/${service.serviceId}`, { state: { merchantId: service.merchantId } });

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('table.service')}</TableHead>
          {showMerchant && <TableHead>{t('table.merchant')}</TableHead>}
          <TableHead>{t('table.amount')}</TableHead>
          <TableHead>{t('table.flow')}</TableHead>
          <TableHead>{t('table.routingKey')}</TableHead>
          <TableHead>{t('table.routing')}</TableHead>
          <TableHead>{t('table.status')}</TableHead>
          <TableHead className="w-[1%]">
            <span className="sr-only">{t('table.actions')}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {services.map((service) => {
          const primaryLabel = service.displayName || service.description || service.serviceId;
          const flow = service.fallbackChain?.[0]?.flowType ?? '—';
          const routingKey =
            service.gatewayServiceId ?? service.fallbackChain?.[0]?.gatewayServiceId ?? '—';
          return (
            <TableRow
              key={service.serviceId}
              className="cursor-pointer touch-manipulation hover:bg-muted/50 coarse:h-14"
              role="button"
              tabIndex={0}
              onClick={() => openService(service)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openService(service);
                }
              }}
            >
              <TableCell className="font-medium">
                <div>{primaryLabel}</div>
                <div className="font-mono text-xs text-muted-foreground">{service.serviceId}</div>
              </TableCell>
              {showMerchant && (
                <TableCell>
                  <MerchantCell merchantId={service.merchantId} />
                </TableCell>
              )}
              <TableCell>{formatCents(service.grossAmount)}</TableCell>
              <TableCell>{flow}</TableCell>
              <TableCell className="font-mono text-xs">{routingKey}</TableCell>
              <TableCell>
                <RoutingStatusBadge
                  fallbackChain={service.fallbackChain}
                  gatewayServiceId={service.gatewayServiceId}
                />
              </TableCell>
              <TableCell>
                <Badge variant={service.status === 'active' ? 'success' : 'secondary'}>
                  {service.status === 'active'
                    ? t('common:status.active')
                    : t('common:status.inactive')}
                </Badge>
              </TableCell>
              {/* Spec 514: die ganze Zelle ist stopPropagation-Zone — coarse:p-3 gibt
                  dem „⋯"-Trigger auf Touch eine breitere neutrale Pufferflaeche,
                  damit knappe Fehl-Taps nicht die Zeilen-Navigation ausloesen. */}
              <TableCell onClick={(e) => e.stopPropagation()} className="coarse:p-3">
                <ServiceActions service={service} onEdit={onEdit} onDelete={onDelete} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
